import { constants } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import { Text, textVariants } from '@etg/wings';
import { css } from '@eti/styles';
import { screenReaderOnly } from '../../../common/utils/styles';
import { focusOnFirstCharacteristicButton } from '../../utils/focusUtils';
import { getSeatCharacteristicLabels } from './seatCharacteristicText';
import { getNameInitials } from './SeatRow.utils';
import { iconMap } from '../../constants/iconMap';
import { rowItemSize } from '../../utils/styles';
import BookableSeat from './BookableSeat';
import ExitArrow from './ExitArrow';
import Stairs from './Stairs';
import Toilet from './Toilet';
import UnavailableSeat from './UnavailableSeat';
import Wall from './Wall';
import Wing from './Wing';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const rowStyles = css`
  display: flex;
  justify-content: center;
  margin: 0 auto;
  width: fit-content;

  html[dir='rtl'] & {
    flex-direction: row-reverse;
  }
`;
const rowWrapperStyles = css`
  display: flex;

  html[dir='rtl'] & {
    flex-direction: row-reverse;
  }

  & > * {
    margin: 5px 0;

    &:not(:last-child) {
      margin-right: 4px;
    }
  }
`;
const aisleStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;
  ${rowItemSize};
`;
const Aisle = ({
  row
}) => /*#__PURE__*/_jsx(Text, {
  className: aisleStyles,
  "data-testid": "aisle",
  variant: textVariants._14_BOLD,
  children: row > 0 && row
});
const exitRowClass = css`
  height: 22px;
  margin-bottom: 0;
  margin-top: 0;
  width: 32px;
`;
const emptySpaceStyles = exitRow => css`
  background: transparent;
  ${exitRow ? exitRowClass : rowItemSize};
`;
const EmptySpace = ({
  isExitRow
}) => /*#__PURE__*/_jsx("div", {
  className: emptySpaceStyles(isExitRow),
  "data-testid": "emptySpace"
});
const RowItem = ({
  activeSeatAssignments,
  carrier,
  index,
  isChild,
  isEmptyRow,
  isExitRow,
  isRyanairInTrip,
  item,
  onChange,
  seatIcons
}) => {
  const {
    t
  } = useTranslation();
  switch (item === null || item === void 0 ? void 0 : item.type) {
    case 'TOILET':
      return /*#__PURE__*/_jsx(Toilet, {});
    case 'STAIRS':
      return /*#__PURE__*/_jsx(Stairs, {});
    case 'AISLE':
      return /*#__PURE__*/_jsx(Aisle, {
        row: item.row
      });
    case 'EXIT_ARROW':
      return /*#__PURE__*/_jsx(ExitArrow, {
        direction: index === 0 ? ExitArrow.Direction.LEFT : ExitArrow.Direction.RIGHT
      });
    case 'NORMAL':
      {
        var _activeSeatAssignment, _activeSeatAssignment2, _iconMap$item$mainCha, _seatIcons$iconKey, _item$markup, _item$price;
        const [seatTypeLabel] = getSeatCharacteristicLabels(item.mainCharacteristic, !item.isBookable, t);
        if (!item.isBookable || isExitRow && isChild) {
          return /*#__PURE__*/_jsx(UnavailableSeat, {
            seatTypeLabel: seatTypeLabel
          });
        }
        const isSelected = activeSeatAssignments.some(s => s.value === item.seat);
        const selectedTravelerName = isSelected ? (_activeSeatAssignment = (_activeSeatAssignment2 = activeSeatAssignments.find(s => s.value === item.seat)) === null || _activeSeatAssignment2 === void 0 ? void 0 : _activeSeatAssignment2.name) !== null && _activeSeatAssignment !== void 0 ? _activeSeatAssignment : null : null;
        const selectedSeatInitials = selectedTravelerName ? getNameInitials(selectedTravelerName) : null;
        const iconKey = isSelected ? 'selected' : (_iconMap$item$mainCha = iconMap[item.mainCharacteristic]) !== null && _iconMap$item$mainCha !== void 0 ? _iconMap$item$mainCha : 'default';
        const SeatIcon = (_seatIcons$iconKey = seatIcons[iconKey]) !== null && _seatIcons$iconKey !== void 0 ? _seatIcons$iconKey : null;
        return /*#__PURE__*/_jsx(BookableSeat, {
          SeatIcon: SeatIcon,
          carrier: carrier,
          column: item.column,
          isRyanairInTrip: isRyanairInTrip,
          isSelected: isSelected,
          markup: (_item$markup = item.markup) !== null && _item$markup !== void 0 ? _item$markup : null,
          onChange: onChange,
          price: (_item$price = item.price) !== null && _item$price !== void 0 ? _item$price : 0,
          row: item.row,
          seat: item.seat,
          seatTypeLabel: seatTypeLabel,
          selectedSeatInitials: selectedSeatInitials
        });
      }
    default:
      return /*#__PURE__*/_jsx(EmptySpace, {
        isExitRow: isEmptyRow
      });
  }
};
const SeatRow = ({
  activeSeatAssignments = [],
  carrier,
  isEmptyRow = false,
  isExitRow = false,
  isOverWing = false,
  isRyanairInTrip,
  onChange,
  onSkipButtonFocus,
  onSkipButtonHover,
  seatIcons,
  seatTravelers = [],
  sellSeatMapSeats = []
}) => {
  const {
    t
  } = useTranslation();
  const isChild = seatTravelers.some(traveler => traveler.ageType === constants.ageTypes.CHILD);
  const hasExitArrow = sellSeatMapSeats.some(seat => (seat === null || seat === void 0 ? void 0 : seat.type) === 'EXIT_ARROW');
  return /*#__PURE__*/_jsxs("div", {
    className: rowStyles,
    children: [isOverWing && /*#__PURE__*/_jsx(Wing, {
      isEmptyRow: isEmptyRow,
      isLeft: true
    }), /*#__PURE__*/_jsx(Wall, {
      "data-testid": "leftWall",
      isEmptyRow: isEmptyRow,
      isLeft: true,
      isOverWing: isOverWing
    }), /*#__PURE__*/_jsx("div", {
      className: rowWrapperStyles,
      children: sellSeatMapSeats.map((item, index) => /*#__PURE__*/_jsx(RowItem, {
        activeSeatAssignments: activeSeatAssignments,
        carrier: carrier,
        index: index,
        isChild: isChild,
        isEmptyRow: isEmptyRow,
        isExitRow: isExitRow,
        isRyanairInTrip: isRyanairInTrip,
        item: item,
        onChange: onChange,
        seatIcons: seatIcons
      }, item.seat || `${item.type}-${item.row}-${index}`))
    }), !hasExitArrow && /*#__PURE__*/_jsx("button", {
      className: screenReaderOnly,
      "data-testid": "accessibilitySkipToNextSegment",
      onBlur: () => onSkipButtonFocus(false),
      onClick: focusOnFirstCharacteristicButton,
      onFocus: () => onSkipButtonFocus(true),
      onMouseEnter: () => onSkipButtonHover(true),
      onMouseLeave: () => onSkipButtonHover(false),
      type: "button",
      children: t('Product.SeatMap.Accessibility.SkipToSeatMapLegend.Text')
    }), /*#__PURE__*/_jsx(Wall, {
      "data-testid": "rightWall",
      isEmptyRow: isEmptyRow,
      isOverWing: isOverWing,
      isRight: true
    }), isOverWing && /*#__PURE__*/_jsx(Wing, {
      isEmptyRow: isEmptyRow,
      isRight: true
    })]
  });
};
export default SeatRow;
//# sourceMappingURL=SeatRow.js.map